import React from 'react'
import { MultiValue } from 'react-select'
import { BasicSelect, BasicSelectProps } from '../BasicSelect/BasicSelect'
import { TagsSelect } from '../TagsSelect/TagsSelect'
import { UseSelectWithPaginationProps, useSelectWithPaginationState } from './useSelectWithPaginationState'
import { useIntersectionObserverForSelect } from './useIntersectionObserverForSelect'

export type MultiSelectWithPaginationProps<Option> = UseSelectWithPaginationProps<Option> &
  Omit<BasicSelectProps<Option, true>, 'options' | 'isMulti' | 'value'> & {
    value?: MultiValue<Option>
    /**
     * Whether or not to hide outside values.
     */
    disableTagsOutside?: boolean
    /**
     * If the number of selected values will be greater than this, then the outside tags will be collapsed.
     */
    collapseCount?: number
  }

/**
 * MultiSelect with pagination. First mount calls onUpdateRequested with { page: 1 }.
 * Next, on scroll to the end calls onUpdateRequested with { page: page + 1 }.
 * New options should be passed to the prop newOptions (when the reference is changed this array is added to the end of the list).
 */
export const MultiSelectWithPagination = <Option extends any>({
  initialOptions,
  initialPage,
  onUpdateRequested,
  pageSize,
  newOptions,
  total,
  progress,
  clearDeps,
  disableTagsOutside,
  disableTags,
  menuMaxHeight = '50vh',
  onChange,
  ...props
}: MultiSelectWithPaginationProps<Option>) => {
  const { options, setSearch, fetchNextPage, search } = useSelectWithPaginationState({
    initialOptions,
    initialPage,
    onUpdateRequested,
    newOptions,
    total,
    progress,
    pageSize,
    clearDeps,
  })

  const intersectionProps = useIntersectionObserverForSelect<Option, true, any>({
    ...props,
    menuMaxHeight,
    onMenuScrollToBottom: fetchNextPage,
  })

  return disableTagsOutside || disableTags ? (
    <BasicSelect
      isMulti
      onMenuScrollToBottom={fetchNextPage}
      isSearchable
      progress={progress}
      options={options}
      onSearchChange={setSearch}
      searchValue={search}
      disableTags={disableTags}
      onChange={onChange}
      {...intersectionProps}
      {...props}
    />
  ) : (
    <TagsSelect
      isSearchable
      onMenuScrollToBottom={fetchNextPage}
      progress={progress}
      options={options}
      onSearchChange={setSearch}
      searchValue={search}
      onChange={onChange}
      {...intersectionProps}
      {...props}
    />
  )
}
